import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Link } from 'react-router-dom';
import ExcelJS from 'exceljs';
import reportService from '../../services/reportService';
import { getWarehouses } from '../../services/warehousesService';
import Toast from '../../components/Common/Toast';
import ConfirmModal from '../../components/Common/ConfirmModal';
import { useCanMutate, isLimitedReadOnlyUser } from '../../hooks/useCanMutate';
import { authService } from '../../services/authService';
import { formatDateLocaleLima } from '../../utils/dateUtils';
import { auditRuleLabel } from '../../utils/auditRuleCatalog';
import { formatGuideSaveMessage, buildRecalculateRunProgressMessage, recalculateRunToastType } from '../../utils/kardexRecalculationHelpers';
import notificationService from '../../services/notificationService';

const eventLabels = {
  compra: 'Compra',
  venta: 'Venta',
  saldo_inicial: 'Saldo Inicial',
  traspaso: 'Traspaso',
  recepcion: 'Recepción',
  transferencia_gratuita: 'Transferencia Gratuita',
  descuento_obtenido: 'Descuento Obtenido',
  descuento_concedido: 'Descuento Concedido',
  descuento_con_mercaderia: 'Descuento con Mercadería',
  consumo: 'Consumo',
  devolucion: 'Devolución',
  salida_produccion: 'Salida a producción',
  entrada_produccion: 'Entrada de producción',
  entrada_producto_terminado: 'Entrada producto terminado',
  ajuste_inventario: 'Ajuste inventario',
  ajuste_costo: 'Ajuste de costo',
};

const formatMoney4 = (n) => {
  if (n === null || n === undefined || Number.isNaN(Number(n))) return '-';
  return `S/ ${Number(n).toLocaleString('es-PE', { minimumFractionDigits: 4, maximumFractionDigits: 4 })}`;
};

const pairsFromInconsistencyReport = (rows) => (rows || []).map((item) => ({
  product_id: item.product.id,
  warehouse_id: item.warehouse.id,
}));

const sortInconsistencyRows = (rows) => [...rows].sort((a, b) => {
  const nameCmp = (a.product?.name || '').localeCompare(b.product?.name || '');
  if (nameCmp !== 0) return nameCmp;
  return (a.warehouse?.code || '').localeCompare(b.warehouse?.code || '');
});

const mergeInconsistencyRows = (existing, incoming) => {
  const map = new Map();
  [...existing, ...incoming].forEach((row) => {
    const key = `${row.product.id}-${row.warehouse.id}`;
    map.set(key, row);
  });
  return sortInconsistencyRows(Array.from(map.values()));
};

const formatExportDate = () => new Date().toISOString().slice(0, 10);

const styleExcelHeaderRow = (row) => {
  row.eachCell((cell) => {
    cell.font = { bold: true };
    cell.fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: 'FFF3F4F6' },
    };
  });
};

const KARDEX_INCONSISTENCY_RULES = [
  'stock_negativo',
  'valor_negativo',
  'salida_costo_incorrecto',
  'linea_total_inconsistente',
  'salida_vaciado_stock',
  'recepcion_costo_traspaso',
  'entrada_costo_cero',
  'costo_temporal_aplicado',
  'costo_temporal_sin_referencia',
  'costo_temporal_heredado_traspaso',
];

const movementMatchesRule = (movement, ruleCode) => {
  if (!ruleCode) return true;
  if (Array.isArray(movement?.revision_codes) && movement.revision_codes.includes(ruleCode)) {
    return true;
  }
  const label = auditRuleLabel(ruleCode, '').toLowerCase();
  const revision = String(movement?.revision || '').toLowerCase();
  return label && revision.includes(label);
};

const pairMatchesFilters = (item, filters) => {
  const search = filters.search.trim().toLowerCase();
  if (search) {
    const name = String(item.product?.name || '').toLowerCase();
    const sku = String(item.product?.sku || '').toLowerCase();
    if (!name.includes(search) && !sku.includes(search)) {
      return false;
    }
  }

  if (filters.warehouseId && String(item.warehouse?.id) !== String(filters.warehouseId)) {
    return false;
  }

  if (filters.ruleCode) {
    const movements = item.movements || [];
    if (!movements.some((movement) => movementMatchesRule(movement, filters.ruleCode))) {
      return false;
    }
  }

  return true;
};

const KardexInconsistencies = () => {
  const canMutate = useCanMutate();
  const isLimited = isLimitedReadOnlyUser(authService.getCurrentUser());
  const [data, setData] = useState([]);
  const [countFrom, setCountFrom] = useState(null);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState({
    isOpen: false,
    message: '',
    type: 'info',
    progress: null,
    persistent: false,
    duration: 5000,
  });
  const [expandedId, setExpandedId] = useState(null);
  const [preview, setPreview] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [confirmBulkOpen, setConfirmBulkOpen] = useState(false);
  const [bulkRunning, setBulkRunning] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [scanProgress, setScanProgress] = useState({ scanned: 0, total: 0, percent: 0 });
  const [foundPairsCount, setFoundPairsCount] = useState(0);
  const [exporting, setExporting] = useState(false);
  const [warehouses, setWarehouses] = useState([]);
  const [filters, setFilters] = useState({
    search: '',
    warehouseId: '',
    ruleCode: '',
  });
  const hasLoaded = useRef(false);
  const scanCancelRef = useRef(false);
  const recalcPollRef = useRef(null);

  const filteredData = useMemo(
    () => data.filter((item) => pairMatchesFilters(item, filters)),
    [data, filters]
  );

  const hasActiveFilters = Boolean(filters.search.trim() || filters.warehouseId || filters.ruleCode);

  useEffect(() => {
    getWarehouses({ active_only: true }).then((res) => {
      if (res.success) setWarehouses(res.data || []);
    });
  }, []);

  useEffect(() => {
    if (!hasLoaded.current) {
      hasLoaded.current = true;
      loadReport();
    }
  }, []);

  useEffect(() => () => {
    if (recalcPollRef.current) {
      clearTimeout(recalcPollRef.current);
    }
  }, []);

  const pollRecalculateRun = (runId, pairsTotal) => {
    const poll = async () => {
      try {
        const res = await notificationService.getKardexRecalculateRun(runId);
        if (!res?.success || !res.data) {
          return;
        }

        const run = res.data;
        const progress = run.progress_percent ?? (run.is_active ? 10 : 100);

        if (run.is_active) {
          setToast({
            isOpen: true,
            type: 'info',
            persistent: true,
            progress,
            duration: 5000,
            message: buildRecalculateRunProgressMessage(run, pairsTotal),
          });
          recalcPollRef.current = setTimeout(poll, 1200);
          return;
        }

        setToast({
          isOpen: true,
          type: recalculateRunToastType(run),
          persistent: false,
          progress: 100,
          duration: 8000,
          message: buildRecalculateRunProgressMessage(run, pairsTotal),
        });
        await loadReport();
      } catch (error) {
        console.error('Error al consultar progreso de recálculo:', error);
        setToast({
          isOpen: true,
          type: 'warning',
          persistent: false,
          progress: null,
          duration: 7000,
          message: 'Recálculo en segundo plano. Revise notificaciones para el resultado.',
        });
      }
    };

    setToast({
      isOpen: true,
      type: 'info',
      persistent: true,
      progress: 5,
      duration: 5000,
      message: `Recálculo en cola… ${pairsTotal} par(es)`,
    });

    recalcPollRef.current = setTimeout(poll, 800);
  };

  const loadReport = async () => {
    scanCancelRef.current = false;
    setScanning(true);
    setLoading(true);
    setData([]);
    setFoundPairsCount(0);
    setScanProgress({ scanned: 0, total: 0, percent: 0 });

    try {
      const meta = await reportService.getKardexInconsistenciesScanMeta();
      if (!meta.success) {
        setToast({ isOpen: true, message: meta.message || 'Error al iniciar escaneo', type: 'error' });
        return;
      }

      setCountFrom(meta.count_from || null);
      const totalPairs = meta.total_pairs || 0;
      const batchSize = meta.default_batch_size || 200;
      setScanProgress({ scanned: 0, total: totalPairs, percent: 0 });

      if (totalPairs === 0) {
        setData([]);
        return;
      }

      let offset = 0;
      let accumulated = [];

      while (offset < totalPairs) {
        if (scanCancelRef.current) {
          break;
        }

        const batch = await reportService.scanKardexInconsistenciesBatch({
          offset,
          batchSize,
          totalPairs,
        });

        if (!batch.success) {
          setToast({ isOpen: true, message: batch.message || 'Error durante el escaneo', type: 'error' });
          break;
        }

        const batchRows = batch.data || [];
        if (batchRows.length > 0) {
          accumulated = mergeInconsistencyRows(accumulated, batchRows);
          setData(accumulated);
          setFoundPairsCount(accumulated.length);
        }

        const progress = batch.progress || {};
        setScanProgress({
          scanned: progress.scanned ?? offset,
          total: progress.total_pairs ?? totalPairs,
          percent: progress.percent ?? 0,
        });

        if (progress.done) {
          break;
        }

        offset = progress.scanned ?? (offset + batchSize);
      }
    } catch (error) {
      console.error('Error al cargar inconsistencias:', error);
      setToast({ isOpen: true, message: 'Error al escanear inconsistencias de kardex', type: 'error' });
    } finally {
      setLoading(false);
      setScanning(false);
    }
  };

  const cancelScan = () => {
    scanCancelRef.current = true;
    setScanning(false);
    setLoading(false);
  };

  const toggleExpand = (id) => {
    setExpandedId(prev => prev === id ? null : id);
  };

  const loadPreview = async () => {
    if (filteredData.length === 0) {
      setToast({ isOpen: true, message: 'No hay pares con inconsistencias para recalcular.', type: 'info' });
      return;
    }

    setLoadingPreview(true);
    try {
      const pairs = pairsFromInconsistencyReport(filteredData);
      const res = await reportService.previewRecalculateAllKardex({ pairs });
      if (res.success) {
        setPreview(res.data);
        setConfirmBulkOpen(true);
      } else {
        setToast({ isOpen: true, message: res.message || 'No se pudo cargar la vista previa', type: 'error' });
      }
    } catch (e) {
      setToast({
        isOpen: true,
        message: e.response?.data?.message || 'Error al preparar recálculo masivo',
        type: 'error',
      });
    } finally {
      setLoadingPreview(false);
    }
  };

  const exportToExcel = async () => {
    if (filteredData.length === 0) {
      setToast({ isOpen: true, message: 'No hay datos para exportar.', type: 'info' });
      return;
    }

    setExporting(true);
    try {
      const workbook = new ExcelJS.Workbook();
      workbook.creator = 'Contifine Logistic';
      workbook.created = new Date();

      const summarySheet = workbook.addWorksheet('Resumen', { views: [{ state: 'frozen', ySplit: 1 }] });
      summarySheet.addRow([
        'Producto',
        'SKU',
        'Código almacén',
        'Almacén',
        'Nº inconsistencias',
      ]);
      styleExcelHeaderRow(summarySheet.getRow(1));

      const detailSheet = workbook.addWorksheet('Detalle', { views: [{ state: 'frozen', ySplit: 1 }] });
      detailSheet.addRow([
        'Producto',
        'SKU',
        'Código almacén',
        'Almacén',
        'Fecha',
        'Hora',
        'Evento',
        'Tipo',
        'Guía',
        'Cantidad',
        'Costo unitario',
        'Total',
        'Costo recepción',
        'Costo traspaso',
        'Inconsistencia',
      ]);
      styleExcelHeaderRow(detailSheet.getRow(1));

      filteredData.forEach((item) => {
        const movements = filters.ruleCode
          ? (item.movements || []).filter((mov) => movementMatchesRule(mov, filters.ruleCode))
          : (item.movements || []);

        summarySheet.addRow([
          item.product?.name || '',
          item.product?.sku || '',
          item.warehouse?.code || '',
          item.warehouse?.name || '',
          movements.length || item.inconsistencies_count || 0,
        ]);

        movements.forEach((mov) => {
          detailSheet.addRow([
            item.product?.name || '',
            item.product?.sku || '',
            item.warehouse?.code || '',
            item.warehouse?.name || '',
            formatDateLocaleLima(mov.movement_date),
            mov.movement_time || '',
            eventLabels[mov.kardex_event] || mov.kardex_event || '',
            mov.movement_type === 'entrada' ? 'Entrada' : mov.movement_type === 'salida' ? 'Salida' : (mov.movement_type || ''),
            mov.entry_guide_code || mov.guide_number || '',
            mov.quantity ?? '',
            mov.unit_price ?? '',
            mov.total ?? '',
            mov.reception_unit_price ?? '',
            mov.transfer_unit_price ?? '',
            mov.revision || '',
          ]);
        });
      });

      [summarySheet, detailSheet].forEach((sheet) => {
        sheet.columns.forEach((column) => {
          let maxLength = 10;
          column.eachCell({ includeEmpty: true }, (cell) => {
            const value = cell.value == null ? '' : String(cell.value);
            maxLength = Math.max(maxLength, value.length);
          });
          column.width = Math.min(maxLength + 2, 48);
        });
      });

      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Inconsistencias_Kardex_${formatExportDate()}.xlsx`;
      link.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error al exportar inconsistencias de kardex:', error);
      setToast({ isOpen: true, message: 'No se pudo exportar el archivo Excel.', type: 'error' });
    } finally {
      setExporting(false);
    }
  };

  const executeBulkRecalculate = async () => {
    setBulkRunning(true);
    try {
      const pairs = pairsFromInconsistencyReport(filteredData);
      const res = await reportService.recalculateAllKardex({ pairs });
      if (res.success) {
        setConfirmBulkOpen(false);
        setPreview(null);
        const d = res.data || {};
        const queued = res.kardex_recalculation?.status === 'queued';
        const pairsTotal = d.pairs_total ?? preview?.processed_pairs ?? pairs.length ?? 0;
        const runId = res.kardex_recalculation?.run_id;

        if (queued && runId) {
          pollRecalculateRun(runId, pairsTotal);
        } else {
          setToast({
            isOpen: true,
            message: formatGuideSaveMessage(
              res.message ||
                `Procesados: ${d.processed_pairs ?? 0}. Omitidos: ${d.skipped_pairs ?? 0}. Movimientos: ${d.updated_movements ?? 0}.`,
              res
            ),
            type: 'success',
            progress: null,
            persistent: false,
            duration: 6000,
          });
          await loadReport();
        }
      } else {
        setToast({ isOpen: true, message: res.message || 'No se pudo recalcular', type: 'error' });
      }
    } catch (e) {
      setToast({
        isOpen: true,
        message: e.response?.data?.message || 'Error al recalcular kardex masivamente',
        type: 'error',
      });
    } finally {
      setBulkRunning(false);
    }
  };

  return (
    <div className="p-6">
      <div className="mb-6">
        <Link
          to="/reports/kardex"
          className="inline-flex items-center gap-2 text-gray-600 hover:text-teal-700 mb-4"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          Volver a Reportes
        </Link>
        <h1 className="text-2xl font-bold text-gray-800">Inconsistencias de Kardex</h1>
        <p className="text-gray-600 mt-1 max-w-3xl">
          Productos y almacenes con movimientos que presentan inconsistencias (costo unitario incorrecto, costo de recepción, stock o valor negativo).
        </p>
        {countFrom?.enabled && countFrom?.date ? (
          <div className="mt-3 max-w-3xl rounded-md border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-900">
            Contando inconsistencias desde{' '}
            <strong>
              {formatDateLocaleLima(countFrom.date)}
              {countFrom.time ? ` ${String(countFrom.time).slice(0, 5)}` : ''}
            </strong>
            . Los movimientos anteriores no se reportan como inconsistencia.
          </div>
        ) : null}
        <div className="mt-4 flex flex-wrap items-center gap-3">
          {data.length > 0 ? (
            <button
              type="button"
              onClick={exportToExcel}
              disabled={exporting || scanning}
              className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 hover:bg-gray-50 disabled:opacity-50 text-gray-800 rounded-md text-sm font-medium"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              {exporting ? 'Exportando…' : 'Exportar Excel'}
            </button>
          ) : null}
        {canMutate ? (
          <>
            <button
              type="button"
              onClick={loadPreview}
              disabled={loadingPreview || bulkRunning || scanning || filteredData.length === 0}
              className="inline-flex items-center gap-2 px-4 py-2 bg-teal-600 hover:bg-teal-700 disabled:opacity-50 text-white rounded-md text-sm font-medium"
            >
              {loadingPreview ? 'Preparando…' : 'Recalcular todos (excepto stock negativo)'}
            </button>
            <button
              type="button"
              onClick={loadReport}
              disabled={scanning || bulkRunning}
              className="inline-flex items-center gap-2 px-4 py-2 border border-gray-300 hover:bg-gray-50 disabled:opacity-50 text-gray-700 rounded-md text-sm font-medium"
            >
              {scanning ? 'Escaneando…' : 'Volver a escanear'}
            </button>
            {scanning ? (
              <button
                type="button"
                onClick={cancelScan}
                className="inline-flex items-center gap-2 px-4 py-2 border border-red-200 text-red-700 hover:bg-red-50 rounded-md text-sm font-medium"
              >
                Detener escaneo
              </button>
            ) : null}
            <p className="text-xs text-gray-500 w-full">
              {isLimited
                ? 'Solo se recalculan los productos con inconsistencias en sus almacenes asignados. Los pares con stock negativo se omiten aquí; use recálculo unitario desde «Ir al kardex».'
                : 'Corrige costos de salidas, recepciones, devoluciones y ajustes. Los pares con stock negativo no entran al masivo: abra el kardex del producto y use recálculo unitario.'}
            </p>
          </>
        ) : null}
        </div>
      </div>

      {(scanning || (loading && scanProgress.total > 0)) ? (
        <div className="mb-6 bg-white rounded-lg shadow-sm border border-teal-200 p-5">
          <div className="flex flex-wrap items-start justify-between gap-3 mb-3">
            <div>
              <h2 className="text-sm font-semibold text-gray-800">Escaneando kardex…</h2>
              <p className="text-sm text-gray-600 mt-1">
                Revisando productos y almacenes para detectar inconsistencias de costo y stock.
              </p>
            </div>
            <div className="text-right text-sm text-gray-700">
              <div className="font-semibold text-teal-700">{scanProgress.percent}%</div>
              <div>
                {scanProgress.scanned.toLocaleString('es-PE')} / {scanProgress.total.toLocaleString('es-PE')} pares
              </div>
            </div>
          </div>
          <div className="h-2.5 w-full rounded-full bg-gray-100 overflow-hidden">
            <div
              className="h-full bg-teal-600 transition-all duration-300 ease-out"
              style={{ width: `${Math.min(100, Math.max(0, scanProgress.percent))}%` }}
            />
          </div>
          <p className="text-xs text-gray-500 mt-3">
            Inconsistencias encontradas hasta ahora: <strong>{foundPairsCount}</strong>
            {foundPairsCount > 0 ? ' — puede expandir filas mientras continúa el escaneo.' : ''}
          </p>
        </div>
      ) : null}

      {loading && scanProgress.total === 0 ? (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12">
          <div className="flex justify-center">
            <div className="w-8 h-8 border-2 border-teal-500 border-t-transparent rounded-full animate-spin" />
          </div>
          <p className="text-center text-gray-500 mt-4">Preparando escaneo…</p>
        </div>
      ) : !scanning && !loading && data.length === 0 ? (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
          <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-green-100 text-green-600 mb-4">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h2 className="text-lg font-semibold text-gray-800">No hay inconsistencias</h2>
          <p className="text-gray-600 mt-2">No se encontraron productos con inconsistencias en el kardex.</p>
        </div>
      ) : data.length > 0 ? (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          <div className="px-4 py-3 border-b border-gray-200 bg-gray-50 space-y-3">
            <p className="text-sm text-gray-600">
              <span className="font-semibold">{hasActiveFilters ? filteredData.length : data.length}</span>
              {hasActiveFilters ? (
                <>
                  {' '}de <span className="font-semibold">{data.length}</span> producto(s)-almacén con inconsistencia
                </>
              ) : (
                ' producto(s)-almacén con al menos una inconsistencia'
              )}
              .
              {scanning ? (
                <span className="text-teal-700"> Escaneo en curso ({scanProgress.percent}%).</span>
              ) : null}
            </p>
            <div className="flex flex-wrap items-end gap-3">
              <div className="min-w-[200px] flex-1">
                <label className="block text-xs font-medium text-gray-600 mb-1">Buscar producto o SKU</label>
                <input
                  type="search"
                  value={filters.search}
                  onChange={(e) => setFilters((prev) => ({ ...prev, search: e.target.value }))}
                  placeholder="Nombre o SKU…"
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                />
              </div>
              <div className="min-w-[180px]">
                <label className="block text-xs font-medium text-gray-600 mb-1">Almacén</label>
                <select
                  value={filters.warehouseId}
                  onChange={(e) => setFilters((prev) => ({ ...prev, warehouseId: e.target.value }))}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm bg-white"
                >
                  <option value="">Todos</option>
                  {warehouses.map((warehouse) => (
                    <option key={warehouse.id} value={warehouse.id}>
                      {warehouse.code} – {warehouse.name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="min-w-[220px]">
                <label className="block text-xs font-medium text-gray-600 mb-1">Tipo de inconsistencia</label>
                <select
                  value={filters.ruleCode}
                  onChange={(e) => setFilters((prev) => ({ ...prev, ruleCode: e.target.value }))}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm bg-white"
                >
                  <option value="">Todas</option>
                  {KARDEX_INCONSISTENCY_RULES.map((code) => (
                    <option key={code} value={code}>
                      {auditRuleLabel(code, code)}
                    </option>
                  ))}
                </select>
              </div>
              {hasActiveFilters ? (
                <button
                  type="button"
                  onClick={() => setFilters({ search: '', warehouseId: '', ruleCode: '' })}
                  className="px-3 py-2 text-sm text-gray-600 hover:text-gray-900 border border-gray-300 rounded-md hover:bg-white"
                >
                  Limpiar filtros
                </button>
              ) : null}
            </div>
          </div>
          {filteredData.length === 0 ? (
            <div className="px-4 py-10 text-center text-sm text-gray-500">
              No hay resultados con los filtros actuales.
            </div>
          ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Producto
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    SKU
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Almacén
                  </th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Nº inconsistencias
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Acciones
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredData.map((item) => {
                  const rowId = `${item.product.id}-${item.warehouse.id}`;
                  const isExpanded = expandedId === rowId;
                  const visibleMovements = filters.ruleCode
                    ? (item.movements || []).filter((mov) => movementMatchesRule(mov, filters.ruleCode))
                    : (item.movements || []);
                  return (
                    <React.Fragment key={rowId}>
                      <tr className="hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm font-medium text-gray-900">
                          {item.product.name}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-500">
                          {item.product.sku || '-'}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600">
                          {item.warehouse.code} – {item.warehouse.name}
                        </td>
                        <td className="px-4 py-3 text-center">
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800">
                            {filters.ruleCode ? visibleMovements.length : item.inconsistencies_count}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-sm">
                          <div className="flex items-center gap-2">
                            <button
                              type="button"
                              onClick={() => toggleExpand(rowId)}
                              className="text-teal-600 hover:text-teal-800 font-medium"
                            >
                              {isExpanded ? 'Ocultar detalle' : 'Ver detalle'}
                            </button>
                            <span className="text-gray-300">|</span>
                            <Link
                              to={`/reports/kardex/${item.product.id}/${item.warehouse.id}`}
                              className="text-teal-600 hover:text-teal-800 font-medium"
                            >
                              Ir al kardex
                            </Link>
                          </div>
                        </td>
                      </tr>
                      {isExpanded && (
                        <tr className="bg-amber-50/50">
                          <td colSpan="5" className="px-4 py-3">
                            <div className="text-sm">
                              <p className="font-medium text-gray-700 mb-2">Movimientos con inconsistencia:</p>
                              <div className="overflow-x-auto border border-gray-200 rounded-lg bg-white">
                                <table className="min-w-full text-xs">
                                  <thead>
                                    <tr className="bg-gray-100">
                                      <th className="px-3 py-2 text-left">Fecha</th>
                                      <th className="px-3 py-2 text-left">Hora</th>
                                      <th className="px-3 py-2 text-left">Evento</th>
                                      <th className="px-3 py-2 text-left">Guía</th>
                                      <th className="px-3 py-2 text-left">Referencia</th>
                                      <th className="px-3 py-2 text-right">Costo recepción</th>
                                      <th className="px-3 py-2 text-right">Costo traspaso</th>
                                      <th className="px-3 py-2 text-left">Inconsistencia</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {visibleMovements.map((mov) => (
                                      <tr key={mov.id} className="border-t border-gray-100">
                                        <td className="px-3 py-2">{formatDateLocaleLima(mov.movement_date)}</td>
                                        <td className="px-3 py-2">{mov.movement_time || '-'}</td>
                                        <td className="px-3 py-2">{eventLabels[mov.kardex_event] || mov.kardex_event}</td>
                                        <td className="px-3 py-2">{mov.entry_guide_code || mov.guide_number || '-'}</td>
                                        <td className="px-3 py-2">{mov.reference || '-'}</td>
                                        <td className="px-3 py-2 text-right">{formatMoney4(mov.reception_unit_price)}</td>
                                        <td className="px-3 py-2 text-right">{formatMoney4(mov.transfer_unit_price)}</td>
                                        <td className="px-3 py-2 text-amber-800 font-medium">{mov.revision}</td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
          )}
        </div>
      ) : null}

      <Toast
        isOpen={toast.isOpen}
        message={toast.message}
        type={toast.type}
        progress={toast.progress}
        persistent={toast.persistent}
        duration={toast.duration}
        onClose={() => setToast((prev) => ({ ...prev, isOpen: false, persistent: false, progress: null }))}
      />

      <ConfirmModal
        isOpen={confirmBulkOpen}
        onClose={() => {
          if (!bulkRunning) {
            setConfirmBulkOpen(false);
            setPreview(null);
          }
        }}
        onConfirm={executeBulkRecalculate}
        title="Recalcular todos los kardex"
        message={
          preview ? (
            <div className="text-sm text-gray-700 space-y-2 text-left">
              <p>
                Se recalcularán los pares producto-almacén <strong>sin stock negativo</strong>. Los que tienen stock negativo deben corregirse con <strong>recálculo unitario</strong> (Ir al kardex → Recalcular).
              </p>
              <ul className="list-disc pl-5 space-y-1">
                <li>Pares con cambios a aplicar: <strong>{preview.processed_pairs ?? 0}</strong></li>
                <li>Sin cambios necesarios: <strong>{preview.pairs_without_changes ?? 0}</strong></li>
                <li>Omitidos (stock negativo): <strong>{preview.skipped_pairs ?? 0}</strong></li>
                <li>Movimientos a corregir (estimado): <strong>{preview.updated_movements ?? 0}</strong></li>
              </ul>
                {(preview.skipped || []).length > 0 ? (
                <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded px-2 py-1.5">
                  Los omitidos por stock negativo: abra ese producto-almacén y use recálculo unitario en el detalle del kardex.
                  {preview.skipped_truncated ? ' (Se muestran solo los primeros casos omitidos.)' : ''}
                </p>
              ) : null}
              <p className="text-xs text-gray-600">
                El recálculo se ejecutará en segundo plano; recibirá una notificación al finalizar.
              </p>
              <p>¿Desea continuar?</p>
            </div>
          ) : (
            '¿Desea recalcular todos los kardex elegibles?'
          )
        }
        confirmText={bulkRunning ? 'Procesando…' : 'Sí, recalcular'}
        cancelText="Cancelar"
      />
    </div>
  );
};

export default KardexInconsistencies;
